<script setup lang="ts">
import LeIconButton from "@dsk/components/IconButton/LeIconButton.vue";
import LeMinus from "@dsk/icons/LeMinus.vue";
import LePlus from "@dsk/icons/LePlus.vue";
import { computed, ref } from "vue";

import LeInflectionSchemeHeader from "@/components/articles/dictsys/inflection-schemes/LeInflectionSchemeHeader.vue";
import LeInflectionSchemeTable from "@/components/articles/dictsys/inflection-schemes/LeInflectionSchemeTable.vue";
import { DictsysGermanAdjectiveInflectionSchemeHelper } from "@/helpers";
import type { DictsysGermanAdjectiveInflectionScheme, DictsysInflectionSchemeTable } from "@/types";

const props = defineProps<{
  inflectionScheme: DictsysGermanAdjectiveInflectionScheme;
  language: string;
}>();

interface ExpandSection {
  header: string;
  tables: DictsysInflectionSchemeTable[];
  isOpen: boolean;
  toggle: () => void;
}

const isComparativeOpen = ref<boolean>(false);
const isSuperlativeOpen = ref<boolean>(false);

const comparativeTables = computed<DictsysInflectionSchemeTable[]>(() =>
  DictsysGermanAdjectiveInflectionSchemeHelper.getComparativeTables(props.inflectionScheme)
);

const superlativeTables = computed<DictsysInflectionSchemeTable[]>(() =>
  DictsysGermanAdjectiveInflectionSchemeHelper.getSuperlativeTables(props.inflectionScheme)
);

const expandableSections = computed<ExpandSection[]>(() => {
  const sections: ExpandSection[] = [];

  if (comparativeTables.value) {
    sections.push({
      header: "Komparativ",
      tables: comparativeTables.value,
      isOpen: isComparativeOpen.value,
      toggle: () => (isComparativeOpen.value = !isComparativeOpen.value)
    });
  }

  if (superlativeTables.value) {
    sections.push({
      header: "Superlativ",
      tables: superlativeTables.value,
      isOpen: isSuperlativeOpen.value,
      toggle: () => (isSuperlativeOpen.value = !isSuperlativeOpen.value)
    });
  }

  return sections;
});
</script>

<template>
  <div class="le-german-adjective-inflection-scheme">
    <LeInflectionSchemeHeader
      :header-elements="DictsysGermanAdjectiveInflectionSchemeHelper.getHeaderElements(props.inflectionScheme)"
      :language="props.language"
      class="le-german-adjective-inflection-scheme__header"
    />
    <LeInflectionSchemeTable
      :tables="DictsysGermanAdjectiveInflectionSchemeHelper.getPositiveTables(props.inflectionScheme)"
      :language="props.language"
    />

    <div
      v-for="(expandableSection, index) in expandableSections"
      :key="index"
      class="le-german-adjective-inflection-scheme__expandable-section"
    >
      <div
        class="le-german-adjective-inflection-scheme__expandable-header-container"
        :class="{
          'le-german-adjective-inflection-scheme__expandable-header-container--open': expandableSection.isOpen
        }"
      >
        <span class="le-german-adjective-inflection-scheme__expandable-header">{{ expandableSection.header }}</span>

        <LeIconButton
          color="primary"
          variant="dark"
          :aria-label="`Fold ${expandableSection.header.toLowerCase()} ` + (expandableSection.isOpen ? 'sammen' : 'ud')"
          @click="expandableSection.toggle()"
        >
          <LePlus v-if="!expandableSection.isOpen" />
          <LeMinus v-else />
        </LeIconButton>
      </div>

      <LeInflectionSchemeTable
        v-if="expandableSection.isOpen"
        :tables="expandableSection.tables"
        :language="props.language"
      />
    </div>
  </div>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";
@import "@/assets/styles/dictsys-article";

.le-german-adjective-inflection-scheme {
  display: flex;
  flex-direction: column;

  &__header {
    margin-bottom: 10px;

    #media-breakpoint-up(lg, {
      margin-bottom: 30px;
    });
  }

  &__expandable-section {
    display: flex;
    flex-direction: column;
    width: max-content;
    margin-top: 12px;

    #media-breakpoint-up(lg, {
      margin-bottom: 24px;
    });
  }

  &__expandable-header-container {
    display: flex;
    align-items: center;

    &--open {
      margin-bottom: 12px;
    }
  }

  &__expandable-header {
    margin-right: 16px;
    .dictsys-inflection-scheme-expandable-header();
  }

  &__expandable-tables {
    display: flex;
    flex-direction: column;
  }
}
</style>
